import { useEffect, useState } from 'react'
import CountUp from 'react-countup'

export interface AnimatedNumberProps {
  value: number
  duration?: number
  format?: (n: number) => string
  /** Unique key so the count-up animation only plays once per browser session.
   *  While the component stays mounted, react-countup's preserveValue
   *  automatically re-animates whenever `value` increases (e.g. new team joined). */
  id?: string
}

// Tracks animations that have played within this JS context (survives re-renders
// and SPA navigation without a page reload).
const playedInSession = new Set<string>()

function hasAlreadyPlayed(id: string): boolean {
  if (playedInSession.has(id)) return true
  try {
    return sessionStorage.getItem(`animated:${id}`) === '1'
  } catch {
    return false
  }
}

function markAsPlayed(id: string): void {
  playedInSession.add(id)
  try {
    sessionStorage.setItem(`animated:${id}`, '1')
  } catch {
    // sessionStorage unavailable (private browsing restrictions etc.) — ignore
  }
}

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReduced(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setPrefersReduced(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return prefersReduced
}

function readsReducedMotionSync(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function AnimatedNumber({ value, duration = 1, format, id }: AnimatedNumberProps) {
  const prefersReduced = usePrefersReducedMotion()

  // Read once synchronously on first render so the test environment (and SSR clients)
  // get the right initial output without waiting for the effect to flush.
  const [reducedFromInitial] = useState(readsReducedMotionSync)

  // Lazy-initialised once on mount: has this id already played its animation?
  const [alreadyPlayed] = useState(() => (id ? hasAlreadyPlayed(id) : false))

  const skipAnimation = prefersReduced || reducedFromInitial || alreadyPlayed

  if (skipAnimation) {
    return <>{format ? format(value) : value}</>
  }

  return (
    <CountUp
      end={value}
      duration={duration}
      preserveValue
      formattingFn={format}
      onEnd={() => {
        if (id) markAsPlayed(id)
      }}
    />
  )
}
